import React, { Component, createRef, type MouseEvent, type RefObject } from 'react';

import { alpha, Box } from '@mui/material';
import { type TBlock } from '@wp-block-builder/package';
import { useIsLoopIteration } from '@wp-block-builder/stores/BlockLayoutStore';

import { BRAND_BLUE, BRAND_RED } from '@waypoint/constants';
import { WpRendererError } from '@waypoint/templates';

import {
  useBlock,
  useCurrentBlockId,
  useHoverState,
  useMutateCurrentHover,
  useSetCurrentBlockId,
} from '../../../editor/hooks';

import CurrentSectionTab from './CurrentSectionTab';
import ErrorTooltip from './ErrorTooltip';

type InternalProps = {
  hoverState: 'none' | 'self' | 'ancestor';
  isSelected: boolean;
  isLoopIteration: boolean;
  block: TBlock;
  hoverMutator: ReturnType<typeof useMutateCurrentHover>;
  children: JSX.Element;
  errorChildren: JSX.Element;

  onClick: () => void;
};

type State = {
  currentLiquidError: null | WpRendererError;
};

class UiSectionWrapper extends Component<InternalProps, State> {
  anchorElement: RefObject<HTMLElement> = createRef();

  static getDerivedStateFromError(error: unknown) {
    if (error instanceof WpRendererError) {
      return { currentLiquidError: error };
    }
    throw error;
  }

  constructor(props: InternalProps) {
    super(props);
    this.state = { currentLiquidError: null };
    this.onClick = this.onClick.bind(this);
    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
  }

  componentDidUpdate(prevProps: Readonly<InternalProps>): void {
    if (prevProps.block !== this.props.block) {
      this.setState({ currentLiquidError: null });
    }
  }

  onClick(ev: MouseEvent) {
    ev.stopPropagation();
    ev.preventDefault();
    this.props.onClick();
  }

  onMouseEnter() {
    const { hoverMutator, block } = this.props;
    hoverMutator.push(block);
  }

  onMouseLeave() {
    const { hoverMutator } = this.props;
    hoverMutator.remove(this.props.block.id);
  }

  get isError() {
    return Boolean(this.state.currentLiquidError);
  }

  get errorTooltip(): JSX.Element | null {
    const error = this.state.currentLiquidError;
    if (!error) {
      return null;
    }
    return <ErrorTooltip error={error} />;
  }

  getOutline() {
    if (this.props.isLoopIteration) {
      const loopColor = alpha(BRAND_BLUE, 0.3);
      const loopErrorColor = alpha(BRAND_RED, 0.3);
      if (this.isError) {
        if (this.props.isSelected) {
          return `2px solid ${loopErrorColor}`;
        }
        return `1px solid ${loopErrorColor}`;
      }
      if (this.props.isSelected) {
        return `2px solid ${loopColor}`;
      }
      if (this.props.hoverState === 'self') {
        return `2px solid ${loopColor}`;
      }
      return undefined;
    }

    if (this.isError) {
      if (this.props.isSelected) {
        return `2px solid ${BRAND_RED}`;
      }
      return `1px solid ${BRAND_RED}`;
    }
    if (this.props.isSelected) {
      return `2px solid ${BRAND_BLUE}`;
    }

    if (this.props.hoverState === 'ancestor') {
      return `1px dashed ${BRAND_BLUE}`;
    }
    if (this.props.hoverState === 'self') {
      return `2px solid ${BRAND_BLUE}`;
    }
    return undefined;
  }

  get sectionTab() {
    if (this.props.isLoopIteration) {
      return null;
    }
    if (!this.props.isSelected) {
      return null;
    }
    return <CurrentSectionTab block={this.props.block} error={this.isError} />;
  }

  renderChildren() {
    if (this.state.currentLiquidError) {
      return this.props.errorChildren;
    }
    return this.props.children;
  }

  render() {
    return (
      <Box
        ref={this.anchorElement}
        onClick={this.onClick}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        sx={{
          position: 'relative',
          '&::after': {
            outline: this.getOutline(),
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            content: `""`,
            outlineOffset: '-1px',
            pointerEvents: 'none',
            zIndex: 5,
          },
        }}
      >
        {this.sectionTab}
        {this.renderChildren()}
        {this.errorTooltip}
      </Box>
    );
  }
}

type Props = {
  block: TBlock;
  errorChildren: JSX.Element;
  children: JSX.Element;
};
export default function EditableSection({ block, children, errorChildren }: Props) {
  const isLoopIteration = useIsLoopIteration();
  const hoverState = useHoverState(block);
  const hoverMutator = useMutateCurrentHover();
  const id = useCurrentBlockId();
  const currentBlock = useBlock(id);
  const setCurrentBlockId = useSetCurrentBlockId();

  return (
    <UiSectionWrapper
      isSelected={block === currentBlock}
      hoverState={hoverState}
      isLoopIteration={isLoopIteration}
      block={block}
      hoverMutator={hoverMutator}
      errorChildren={errorChildren}
      onClick={() => {
        setCurrentBlockId(block.id);
      }}
    >
      {children}
    </UiSectionWrapper>
  );
}
